import {
    Button,
    Group,
    MultiSelect,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { AttractionFameEnum } from '@routellers/types/dist/city-attraction/enum/attraction-fame.enum';
import { CityPersonRO } from '@routellers/types/dist/city-person/city-person.ro';
import { PersonDomainEnum } from '@routellers/types/dist/city-person/enum/person-domain.enum';
import { PersonFactKindEnum } from '@routellers/types/dist/city-person/enum/person-fact-kind.enum';
import { PersonLocaleStatusEnum } from '@routellers/types/dist/city-person/enum/person-locale-status.enum';
import { PersonRecognitionEnum } from '@routellers/types/dist/city-person/enum/person-recognition.enum';
import { PersonReviewStatusEnum } from '@routellers/types/dist/city-person/enum/person-review-status.enum';
import { PersonSourceEnum } from '@routellers/types/dist/city-person/enum/person-source.enum';
import { CONTENT_BASE_LANGUAGE } from '@routellers/types/dist/shared/content-base-language';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { ImageTypeEnum } from '@routellers/types/dist/upload/enum/image-type.enum';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import {
    EditorFooter,
    EntityEditorShell,
    LanguageChips,
    PhotoUpload,
    Section,
} from '../components/editor';
import { enumLabel, enumOptions } from '../i18n';
import { contentLanguagesForCountry } from '../utils/content-languages';

import { CitySearchSelect } from './city-search-select';
import {
    emptyFact,
    emptyPersonLocaleDraft,
    findPersonLocale,
    hasPersonLocaleContent,
    PersonLocaleDraft,
    personLocaleToDraft,
} from './person/locale-helpers';

type Props = {
    opened: boolean;
    onClose: () => void;
    personId: number | null;
    onSaved: () => void;
    onCreated?: (id: number) => void;
    onDeleted?: () => void;
};

const reviewColor: Record<PersonReviewStatusEnum, string> = {
    [PersonReviewStatusEnum.APPROVED]: 'green',
    [PersonReviewStatusEnum.NEED_REVIEW]: 'orange',
    [PersonReviewStatusEnum.REJECTED]: 'red',
    [PersonReviewStatusEnum.REJECTED_MCP]: 'red',
};

type FormValues = {
    name: string;
    primaryCityId: number | null;
    domains: string[];
    fame: AttractionFameEnum | null;
    recognition: PersonRecognitionEnum | null;
    reviewStatus: PersonReviewStatusEnum | null;
    rejectionReason: string;
};

export const PersonEditor = ({
    opened,
    onClose,
    personId,
    onSaved,
    onCreated,
    onDeleted,
}: Props) => {
    const { t } = useTranslation();
    const isCreate = personId == null;
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [moderating, setModerating] = useState(false);
    const [person, setPerson] = useState<CityPersonRO | null>(null);
    const [tab, setTab] = useState('details');
    const [dirty, setDirty] = useState(false);
    const [lang, setLang] = useState<Languages>(CONTENT_BASE_LANGUAGE);
    const [localeDraft, setLocaleDraft] = useState<PersonLocaleDraft>(emptyPersonLocaleDraft());
    const [localeBaseline, setLocaleBaseline] = useState('');
    const [photos, setPhotos] = useState<string[]>([]);
    const [coverPhotoIndex, setCoverPhotoIndex] = useState<number | null>(null);
    const [photosBaseline, setPhotosBaseline] = useState('');

    const form = useForm<FormValues>({
        initialValues: {
            name: '',
            primaryCityId: null,
            domains: [PersonDomainEnum.VISUAL_ART],
            fame: AttractionFameEnum.WELL_KNOWN,
            recognition: null,
            reviewStatus: null,
            rejectionReason: '',
        },
    });

    useEffect(() => {
        setDirty(form.isDirty());
    }, [form]);

    const reviewOptions = useMemo(
        () => enumOptions(Object.values(PersonReviewStatusEnum), t, 'enums.personReviewStatus'),
        [t],
    );
    const domainOptions = useMemo(
        () => enumOptions(Object.values(PersonDomainEnum), t, 'enums.personDomain'),
        [t],
    );
    const fameOptions = useMemo(
        () => enumOptions(Object.values(AttractionFameEnum), t, 'enums.attractionFame'),
        [t],
    );
    const recognitionOptions = useMemo(
        () => enumOptions(Object.values(PersonRecognitionEnum), t, 'enums.personRecognition'),
        [t],
    );
    const factKindOptions = useMemo(
        () => enumOptions(Object.values(PersonFactKindEnum), t, 'enums.personFactKind'),
        [t],
    );
    const localeStatusOptions = useMemo(
        () => enumOptions(Object.values(PersonLocaleStatusEnum), t, 'enums.localeStatus'),
        [t],
    );

    const photosDirty = useMemo(
        () => JSON.stringify({ photos, coverPhotoIndex }) !== photosBaseline,
        [photos, coverPhotoIndex, photosBaseline],
    );

    const loadLocale = (entity: CityPersonRO, language: Languages) => {
        const next = personLocaleToDraft(findPersonLocale(entity.locales, language), entity.name);
        setLocaleDraft(next);
        setLocaleBaseline(JSON.stringify(next));
    };

    useEffect(() => {
        if (!opened) return;

        setTab('details');
        setDirty(false);
        setLang(CONTENT_BASE_LANGUAGE);
        setLocaleDraft(emptyPersonLocaleDraft());
        setLocaleBaseline('');
        setPhotos([]);
        setCoverPhotoIndex(null);
        setPhotosBaseline('');

        if (isCreate) {
            setPerson(null);
            form.reset();
            setLoading(false);

            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);

            try {
                const { data } = await api.cityPeopleApi.getOne(personId!);

                if (cancelled || !data) return;

                setPerson(data);
                form.setValues({
                    name: data.name || '',
                    primaryCityId: data.primaryCityId ?? null,
                    domains: data.domains || [],
                    fame: data.fame || AttractionFameEnum.WELL_KNOWN,
                    recognition: data.recognition ?? null,
                    reviewStatus: data.reviewStatus || null,
                    rejectionReason: data.rejectionReason || '',
                });
                form.resetDirty();
                setPhotos(data.photos || []);
                setCoverPhotoIndex(data.coverPhotoIndex ?? null);
                setPhotosBaseline(
                    JSON.stringify({
                        photos: data.photos || [],
                        coverPhotoIndex: data.coverPhotoIndex ?? null,
                    }),
                );
                loadLocale(data, CONTENT_BASE_LANGUAGE);
            } catch (error) {
                notifications.show({ color: 'red', message: apiErrorMessage(error) });
                onClose();
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, personId]);

    const localeDirty = useMemo(
        () => JSON.stringify(localeDraft) !== localeBaseline,
        [localeDraft, localeBaseline],
    );

    const applyEntity = (data: CityPersonRO, keepLang = lang) => {
        setPerson(data);
        form.setValues({
            name: data.name || '',
            primaryCityId: data.primaryCityId ?? null,
            domains: data.domains || [],
            fame: data.fame || AttractionFameEnum.WELL_KNOWN,
            recognition: data.recognition ?? null,
            reviewStatus: data.reviewStatus || null,
            rejectionReason: data.rejectionReason || '',
        });
        form.resetDirty();
        setPhotos(data.photos || []);
        setCoverPhotoIndex(data.coverPhotoIndex ?? null);
        setPhotosBaseline(
            JSON.stringify({
                photos: data.photos || [],
                coverPhotoIndex: data.coverPhotoIndex ?? null,
            }),
        );
        loadLocale(data, keepLang);
    };

    const savePhotos = async () => {
        if (personId == null) return;

        setSaving(true);

        try {
            const { data } = await api.cityPeopleApi.update({
                id: personId,
                data: {
                    photos,
                    coverPhotoIndex: photos.length ? (coverPhotoIndex ?? 0) : null,
                },
            });

            if (data) applyEntity(data);
            notifications.show({ color: 'green', message: t('editors.person.photosSaved') });
            onSaved();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const changeLang = (next: Languages) => {
        if (next === lang) return;

        if (localeDirty && !window.confirm(t('editors.person.discardLocaleConfirm'))) return;

        setLang(next);
        if (person) loadLocale(person, next);
    };

    const save = async () => {
        if (!form.values.name.trim()) {
            notifications.show({ color: 'red', message: t('editors.person.nameRequired') });

            return;
        }

        if (isCreate && !form.values.domains.length) {
            notifications.show({ color: 'red', message: t('editors.person.domainRequired') });

            return;
        }

        if (isCreate && !form.values.fame) {
            notifications.show({ color: 'red', message: t('editors.person.fameRequired') });

            return;
        }

        setSaving(true);

        try {
            if (isCreate) {
                const { data } = await api.cityPeopleApi.create({
                    data: {
                        name: form.values.name.trim(),
                        primaryCityId: form.values.primaryCityId,
                        domains: form.values.domains as PersonDomainEnum[],
                        fame: form.values.fame!,
                        recognition: form.values.recognition,
                        source: PersonSourceEnum.MANUAL,
                    },
                });

                if (data) {
                    notifications.show({ color: 'green', message: t('editors.person.created') });
                    onSaved();
                    onCreated?.(data.id);
                }
            } else {
                const { data } = await api.cityPeopleApi.update({
                    id: personId!,
                    data: {
                        name: form.values.name.trim(),
                        primaryCityId: form.values.primaryCityId,
                        domains: form.values.domains as PersonDomainEnum[],
                        fame: form.values.fame || undefined,
                        recognition: form.values.recognition,
                    },
                });

                if (data) applyEntity(data);
                notifications.show({ color: 'green', message: t('editors.person.saved') });
                onSaved();
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const saveLocale = async (status?: PersonLocaleStatusEnum) => {
        if (personId == null) return;

        setSaving(true);

        try {
            const facts = localeDraft.facts
                .map((f) => ({
                    ...f,
                    title: f.title?.trim() || undefined,
                    body: f.body.trim(),
                }))
                .filter((f) => f.body);

            const { data } = await api.cityPeopleApi.upsertLocale({
                id: personId,
                data: {
                    language: lang,
                    name: localeDraft.name.trim(),
                    knownFor: localeDraft.knownFor.trim() || null,
                    description: localeDraft.description.trim() || null,
                    facts: facts.length ? facts : null,
                    status: status || localeDraft.status,
                },
            });

            if (data) {
                applyEntity(data, lang);
                notifications.show({ color: 'green', message: t('editors.person.localeSaved') });
                onSaved();
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (personId == null) return;

        if (!window.confirm(t('editors.person.deleteConfirm', { id: personId }))) return;

        setDeleting(true);

        try {
            await api.cityPeopleApi.remove(personId);
            notifications.show({ color: 'green', message: t('editors.person.deleted') });
            onDeleted?.();
            onSaved();
            onClose();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setDeleting(false);
        }
    };

    const moderate = async (reviewStatus: PersonReviewStatusEnum) => {
        if (personId == null) return;

        setModerating(true);

        try {
            const { data } = await api.cityPeopleApi.moderate({
                id: personId,
                data: {
                    reviewStatus,
                    rejectionReason:
                        reviewStatus === PersonReviewStatusEnum.REJECTED ||
                        reviewStatus === PersonReviewStatusEnum.REJECTED_MCP
                            ? form.values.rejectionReason.trim() || null
                            : null,
                },
            });

            if (data) applyEntity(data);
            notifications.show({
                color: 'green',
                message:
                    reviewStatus === PersonReviewStatusEnum.APPROVED
                        ? t('editors.person.approved')
                        : t('editors.person.rejected'),
            });
            onSaved();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setModerating(false);
        }
    };

    const contentLanguages = useMemo(
        () => contentLanguagesForCountry(person?.country),
        [person?.country],
    );

    useEffect(() => {
        if (!contentLanguages.includes(lang)) {
            const next = contentLanguages[0] || CONTENT_BASE_LANGUAGE;

            setLang(next);
            if (person) loadLocale(person, next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentLanguages, lang, person]);

    const chips = contentLanguages.map((l) => {
        const locale = findPersonLocale(person?.locales, l);
        let mark: 'empty' | 'filled' | 'reviewed' | 'dirty' = 'empty';

        if (l === lang && localeDirty) mark = 'dirty';
        else if (locale?.status === PersonLocaleStatusEnum.REVIEWED) mark = 'reviewed';
        else if (hasPersonLocaleContent(personLocaleToDraft(locale, person?.name))) mark = 'filled';

        return { value: l, label: l.toUpperCase(), mark };
    });

    return (
        <EntityEditorShell
            opened={opened}
            onClose={onClose}
            loading={loading}
            title={
                isCreate
                    ? t('editors.person.newTitle')
                    : person?.displayName ||
                      person?.name ||
                      t('editors.person.fallbackTitle', { id: personId })
            }
            subtitle={person?.primaryCityTitle || undefined}
            status={
                person?.reviewStatus
                    ? {
                          label: enumLabel(t, 'enums.personReviewStatus', person.reviewStatus),
                          color: reviewColor[person.reviewStatus] || 'gray',
                      }
                    : undefined
            }
            dirty={tab === 'details' ? dirty : tab === 'media' ? photosDirty : localeDirty}
            tabs={
                isCreate
                    ? [{ value: 'details', label: t('editors.person.tabs.details') }]
                    : [
                          {
                              value: 'details',
                              label: t('editors.person.tabs.details'),
                              dirty: tab === 'details' && dirty,
                          },
                          {
                              value: 'media',
                              label: t('editors.person.tabs.media'),
                              dirty: tab === 'media' && photosDirty,
                          },
                          {
                              value: 'locales',
                              label: t('editors.person.tabs.locales'),
                              dirty: tab === 'locales' && localeDirty,
                          },
                          { value: 'review', label: t('editors.person.tabs.review') },
                      ]
            }
            activeTab={tab}
            onTabChange={setTab}
            size='lg'
        >
            {tab === 'details' && (
                <Stack gap='lg' pb={40}>
                    <Section title={t('editors.person.identity.title')}>
                        <TextInput
                            label={t('editors.person.identity.name')}
                            required
                            {...form.getInputProps('name')}
                        />
                        <CitySearchSelect
                            label={t('editors.person.identity.primaryCity')}
                            value={form.values.primaryCityId}
                            onChange={(cityId) => form.setFieldValue('primaryCityId', cityId)}
                            initialLabel={person?.primaryCityTitle}
                        />
                        <MultiSelect
                            label={t('editors.person.identity.domains')}
                            data={domainOptions}
                            {...form.getInputProps('domains')}
                        />
                        <Group grow>
                            <Select
                                label={t('editors.person.identity.fame')}
                                data={fameOptions}
                                {...form.getInputProps('fame')}
                            />
                            <Select
                                label={t('editors.person.identity.recognition')}
                                description={t('editors.person.identity.recognitionHint')}
                                clearable
                                data={recognitionOptions}
                                {...form.getInputProps('recognition')}
                            />
                        </Group>
                    </Section>
                    <EditorFooter
                        onCancel={onClose}
                        onSave={save}
                        saving={saving}
                        saveLabel={isCreate ? t('common.create') : t('common.save')}
                        left={
                            !isCreate ? (
                                <Button
                                    color='red'
                                    variant='light'
                                    loading={deleting}
                                    onClick={remove}
                                >
                                    {t('common.delete')}
                                </Button>
                            ) : undefined
                        }
                    />
                </Stack>
            )}

            {tab === 'media' && !isCreate && (
                <Stack gap='lg' pb={40}>
                    <Section
                        title={t('editors.person.photos.title')}
                        description={t('editors.person.photos.description')}
                    >
                        <PhotoUpload
                            variant='gallery'
                            label={t('editors.person.photos.gallery')}
                            value={photos}
                            onChange={setPhotos}
                            coverIndex={coverPhotoIndex}
                            onCoverIndexChange={setCoverPhotoIndex}
                            imageType={ImageTypeEnum.CITY_PERSON}
                        />
                    </Section>
                    <EditorFooter
                        onCancel={onClose}
                        onSave={savePhotos}
                        saving={saving}
                        saveLabel={t('editors.person.savePhotos')}
                        disabled={!photosDirty}
                    />
                </Stack>
            )}

            {tab === 'locales' && !isCreate && (
                <Stack gap='lg' pb={40}>
                    <LanguageChips
                        languages={chips}
                        value={lang}
                        onChange={(v) => changeLang(v as Languages)}
                    />
                    <Section title={t('editors.person.copy.title')}>
                        <TextInput
                            label={t('editors.person.copy.localeName')}
                            value={localeDraft.name}
                            onChange={(e) =>
                                setLocaleDraft((d) => ({ ...d, name: e.currentTarget.value }))
                            }
                        />
                        <TextInput
                            label={t('editors.person.copy.knownFor')}
                            value={localeDraft.knownFor}
                            onChange={(e) =>
                                setLocaleDraft((d) => ({ ...d, knownFor: e.currentTarget.value }))
                            }
                        />
                        <Textarea
                            label={t('editors.person.copy.description')}
                            minRows={4}
                            autosize
                            value={localeDraft.description}
                            onChange={(e) =>
                                setLocaleDraft((d) => ({
                                    ...d,
                                    description: e.currentTarget.value,
                                }))
                            }
                        />
                    </Section>
                    <Section title={t('editors.person.facts.title')}>
                        <Group justify='space-between'>
                            <Text size='xs' c='dimmed'>
                                {t('editors.person.facts.hint')}
                            </Text>
                            <Button
                                size='xs'
                                variant='light'
                                onClick={() =>
                                    setLocaleDraft((d) => ({
                                        ...d,
                                        facts: [...d.facts, emptyFact()],
                                    }))
                                }
                            >
                                {t('editors.person.facts.addFact')}
                            </Button>
                        </Group>
                        {localeDraft.facts.map((fact, index) => (
                            <Stack key={fact.id} gap='xs'>
                                <Group grow align='flex-end'>
                                    <Select
                                        label={
                                            index === 0 ? t('editors.person.facts.kind') : undefined
                                        }
                                        data={factKindOptions}
                                        value={fact.kind}
                                        onChange={(v) =>
                                            setLocaleDraft((d) => {
                                                const facts = [...d.facts];
                                                facts[index] = {
                                                    ...facts[index],
                                                    kind:
                                                        (v as PersonFactKindEnum) ||
                                                        PersonFactKindEnum.BIO,
                                                };

                                                return { ...d, facts };
                                            })
                                        }
                                    />
                                    <TextInput
                                        label={
                                            index === 0
                                                ? t('editors.person.facts.factTitle')
                                                : undefined
                                        }
                                        value={fact.title || ''}
                                        onChange={(e) =>
                                            setLocaleDraft((d) => {
                                                const facts = [...d.facts];
                                                facts[index] = {
                                                    ...facts[index],
                                                    title: e.currentTarget.value,
                                                };

                                                return { ...d, facts };
                                            })
                                        }
                                    />
                                    <Button
                                        color='red'
                                        variant='subtle'
                                        onClick={() =>
                                            setLocaleDraft((d) => ({
                                                ...d,
                                                facts: d.facts.filter((_, i) => i !== index),
                                            }))
                                        }
                                    >
                                        {t('editors.person.facts.remove')}
                                    </Button>
                                </Group>
                                <Textarea
                                    minRows={2}
                                    value={fact.body}
                                    onChange={(e) =>
                                        setLocaleDraft((d) => {
                                            const facts = [...d.facts];
                                            facts[index] = {
                                                ...facts[index],
                                                body: e.currentTarget.value,
                                            };

                                            return { ...d, facts };
                                        })
                                    }
                                />
                            </Stack>
                        ))}
                        <Select
                            label={t('editors.person.facts.localeStatus')}
                            data={localeStatusOptions}
                            value={localeDraft.status}
                            onChange={(v) =>
                                setLocaleDraft((d) => ({
                                    ...d,
                                    status: (v as PersonLocaleStatusEnum) || d.status,
                                }))
                            }
                        />
                    </Section>
                    <EditorFooter
                        onCancel={onClose}
                        onSave={() => saveLocale()}
                        saving={saving}
                        saveLabel={t('editors.person.saveLocale')}
                        left={
                            <Button
                                variant='light'
                                color='green'
                                loading={saving}
                                onClick={() => saveLocale(PersonLocaleStatusEnum.REVIEWED)}
                            >
                                {t('editors.person.saveMarkReviewed')}
                            </Button>
                        }
                    />
                </Stack>
            )}

            {tab === 'review' && !isCreate && (
                <Stack gap='lg'>
                    <Section title={t('editors.person.moderation.title')}>
                        <Select
                            label={t('editors.person.moderation.reviewStatus')}
                            data={reviewOptions}
                            {...form.getInputProps('reviewStatus')}
                        />
                        <Textarea
                            label={t('editors.person.moderation.rejectionReason')}
                            minRows={3}
                            {...form.getInputProps('rejectionReason')}
                        />
                        <Group justify='flex-end'>
                            <Button
                                color='red'
                                variant='light'
                                loading={moderating}
                                onClick={() => moderate(PersonReviewStatusEnum.REJECTED)}
                            >
                                {t('editors.person.moderation.reject')}
                            </Button>
                            <Button
                                color='green'
                                loading={moderating}
                                onClick={() => moderate(PersonReviewStatusEnum.APPROVED)}
                            >
                                {t('editors.person.moderation.approve')}
                            </Button>
                        </Group>
                    </Section>
                    <EditorFooter onCancel={onClose} />
                </Stack>
            )}
        </EntityEditorShell>
    );
};
